import React, { useState, useEffect } from 'react';
import { Flag, Eye, Trash2, CheckCircle, Search, Filter } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const statusConfig = {
  active: { label: 'Active', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20' },
  pending: { label: 'Pending Review', color: 'text-blue-400 bg-blue-400/10 border-blue-400/20' },
  cancelled: { label: 'Cancelled', color: 'text-red-400 bg-red-400/10 border-red-400/20' },
  sold: { label: 'Sold', color: 'text-green-400 bg-green-400/10 border-green-400/20' },
  expired: { label: 'Expired', color: 'text-gray-400 bg-gray-400/10 border-gray-400/20' },
};

export default function FlaggedListings({ onTakedown }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    loadListings();
  }, []);

  const loadListings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('marketplace_listings')?.select(`
          id,
          title,
          asking_price,
          status,
          condition,
          created_at,
          view_count,
          offer_count,
          featured_image_url,
          user_profiles!marketplace_listings_seller_id_fkey(full_name, email),
          collectibles!marketplace_listings_collectible_id_fkey(collectible_type)
        `)?.order('created_at', { ascending: false })?.limit(50);

      if (!error) {
        setListings(data || []);
      }
    } catch (err) {
      console.error('Error loading listings:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = listings?.filter(l => {
    const matchSearch = l?.title?.toLowerCase()?.includes(search?.toLowerCase()) ||
      l?.user_profiles?.full_name?.toLowerCase()?.includes(search?.toLowerCase()) ||
      l?.user_profiles?.email?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = statusFilter === 'all' || l?.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleAction = async (id, action) => {
    setActionLoading(id);
    try {
      if (action === 'takedown' || action === 'cancel') {
        const { error } = await supabase?.from('marketplace_listings')?.update({ status: 'cancelled', updated_at: new Date()?.toISOString() })?.eq('id', id);
        if (!error) {
          setListings(prev => prev?.map(l => l?.id === id ? { ...l, status: 'cancelled' } : l));
          if (onTakedown) onTakedown(id);
          setSelected(null);
        }
      } else if (action === 'approve') {
        const { error } = await supabase?.from('marketplace_listings')?.update({ status: 'active', updated_at: new Date()?.toISOString() })?.eq('id', id);
        if (!error) {
          setListings(prev => prev?.map(l => l?.id === id ? { ...l, status: 'active' } : l));
          setSelected(null);
        }
      }
    } catch (err) {
      console.error('Action error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[1,2,3]?.map(i => <div key={i} className="h-16 bg-gray-800 rounded-xl border border-gray-700" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search listings or sellers..."
            value={search}
            onChange={e => setSearch(e?.target?.value)}
            className="w-full pl-9 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-gray-400 flex-shrink-0" />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e?.target?.value)}
            className="flex-1 sm:flex-none bg-gray-800 border border-gray-700 rounded-lg text-sm text-white px-3 py-3 focus:outline-none focus:border-blue-500 min-h-[44px]"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
            <option value="sold">Sold</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>
      {/* Count */}
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <Flag size={14} className="text-red-400" />
        <span>{filtered?.length} listings found</span>
      </div>
      {/* Desktop Table */}
      <div className="hidden sm:block overflow-x-auto rounded-xl border border-gray-700">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700 bg-gray-800/60">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Listing</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Seller</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Category</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Views</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {filtered?.map(listing => (
              <tr key={listing?.id} className={`hover:bg-gray-800/40 transition-colors ${selected?.id === listing?.id ? 'bg-blue-500/5' : ''}`}>
                <td className="px-4 py-3">
                  <div>
                    <div className="font-medium text-white truncate max-w-[200px]">{listing?.title}</div>
                    <div className="text-xs text-gray-500">${parseFloat(listing?.asking_price || 0)?.toLocaleString()}</div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-blue-400 text-xs font-medium">{listing?.user_profiles?.full_name || listing?.user_profiles?.email || 'Unknown'}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-orange-300 text-xs capitalize">{listing?.collectibles?.collectible_type?.replace(/-/g, ' ') || '—'}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-gray-300 text-xs">{listing?.view_count || 0}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusConfig?.[listing?.status]?.color || statusConfig?.active?.color}`}>
                    {statusConfig?.[listing?.status]?.label || listing?.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setSelected(selected?.id === listing?.id ? null : listing)}
                      className="p-2 rounded-lg hover:bg-blue-500/10 text-blue-400 transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
                      title="View Details"
                    >
                      <Eye size={14} />
                    </button>
                    {listing?.status === 'active' && (
                      <button
                        onClick={() => handleAction(listing?.id, 'cancel')}
                        disabled={actionLoading === listing?.id}
                        className="p-2 rounded-lg hover:bg-red-500/10 text-red-400 transition-colors disabled:opacity-50 min-w-[36px] min-h-[36px] flex items-center justify-center"
                        title="Cancel Listing"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                    {listing?.status === 'cancelled' && (
                      <button
                        onClick={() => handleAction(listing?.id, 'approve')}
                        disabled={actionLoading === listing?.id}
                        className="p-2 rounded-lg hover:bg-green-500/10 text-green-400 transition-colors disabled:opacity-50 min-w-[36px] min-h-[36px] flex items-center justify-center"
                        title="Reinstate Listing"
                      >
                        <CheckCircle size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered?.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Flag size={32} className="mx-auto mb-3 opacity-30" />
            <p>No listings match your filters</p>
          </div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="sm:hidden space-y-3">
        {filtered?.length === 0 ? (
          <div className="text-center py-12 text-gray-500 bg-gray-800/40 rounded-xl border border-gray-700">
            <Flag size={32} className="mx-auto mb-3 opacity-30" />
            <p>No listings match your filters</p>
          </div>
        ) : (
          filtered?.map(listing => (
            <div key={listing?.id} className={`bg-gray-800/60 border border-gray-700 rounded-xl p-4 ${selected?.id === listing?.id ? 'border-blue-500/40' : ''}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-white text-sm truncate">{listing?.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">${parseFloat(listing?.asking_price || 0)?.toLocaleString()}</p>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border flex-shrink-0 ${statusConfig?.[listing?.status]?.color || statusConfig?.active?.color}`}>
                  {statusConfig?.[listing?.status]?.label || listing?.status}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                <div>
                  <p className="text-gray-500">Seller</p>
                  <p className="text-blue-400 font-medium truncate">{listing?.user_profiles?.full_name || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Category</p>
                  <p className="text-orange-300 capitalize truncate">{listing?.collectibles?.collectible_type?.replace(/-/g, ' ') || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Views</p>
                  <p className="text-gray-300">{listing?.view_count || 0}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelected(selected?.id === listing?.id ? null : listing)}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-blue-500/10 text-blue-400 text-xs font-medium hover:bg-blue-500/20 transition-colors min-h-[44px]"
                >
                  <Eye size={14} />
                  {selected?.id === listing?.id ? 'Hide Details' : 'View Details'}
                </button>
                {listing?.status === 'active' && (
                  <button
                    onClick={() => handleAction(listing?.id, 'cancel')}
                    disabled={actionLoading === listing?.id}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-red-500/10 text-red-400 text-xs font-medium hover:bg-red-500/20 transition-colors disabled:opacity-50 min-h-[44px]"
                  >
                    <Trash2 size={14} />
                    Cancel
                  </button>
                )}
                {listing?.status === 'cancelled' && (
                  <button
                    onClick={() => handleAction(listing?.id, 'approve')}
                    disabled={actionLoading === listing?.id}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-green-500/10 text-green-400 text-xs font-medium hover:bg-green-500/20 transition-colors disabled:opacity-50 min-h-[44px]"
                  >
                    <CheckCircle size={14} />
                    Reinstate
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Detail Panel */}
      {selected && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 sm:p-5 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-white">{selected?.title}</h3>
              <p className="text-xs text-gray-400 mt-0.5">ID: {selected?.id?.slice(0, 8)}... · Listed {new Date(selected.created_at)?.toLocaleDateString()}</p>
            </div>
            <button onClick={() => setSelected(null)} className="text-gray-500 hover:text-gray-300 text-xs p-1 min-w-[32px] min-h-[32px] flex items-center justify-center">✕</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gray-900/50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">Seller</div>
              <div className="text-sm font-medium text-blue-400 truncate">{selected?.user_profiles?.full_name || 'Unknown'}</div>
            </div>
            <div className="bg-gray-900/50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">Price</div>
              <div className="text-sm font-medium text-white">${parseFloat(selected?.asking_price || 0)?.toLocaleString()}</div>
            </div>
            <div className="bg-gray-900/50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">Views</div>
              <div className="text-sm font-medium text-orange-300">{selected?.view_count || 0}</div>
            </div>
            <div className="bg-gray-900/50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">Offers</div>
              <div className="text-sm font-bold text-yellow-400">{selected?.offer_count || 0}</div>
            </div>
          </div>
          {selected?.status === 'active' && (
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => handleAction(selected?.id, 'cancel')}
                disabled={actionLoading === selected?.id}
                className="flex items-center gap-2 px-4 py-2.5 bg-red-600/20 border border-red-600/30 text-red-400 rounded-lg text-sm hover:bg-red-600/30 transition-colors disabled:opacity-50 min-h-[44px]"
              >
                <Trash2 size={14} /> Cancel Listing
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
